/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useEffect, useState, useMemo, useRef } from "react";
import { Button, CircularProgress } from "@mui/material";
import { useDropzone } from "react-dropzone";
import { useAuth } from "cms/auth/hooks/use-auth";
import { SimpliCityAdminSDK } from "@simplicitycms/simplicity-admin-js-sdk";
import { uploadFileAzure } from "cms/documents/upload-document/AzureStorage";
import { DocumentUploadItem } from "cms/documents/upload-document/document-upload-item";
import { Logger } from "cms/utils/log/logger";
import { useOrgSettings } from "hooks/use-org-settings";
import { isTrue, isFalse, isArrayNotEmpty } from "utils/empty-exists";
import {
    autoGenerateFileName, getBasenameAndExtension, isKnownFileType,
    isValidDocumentExtension,
    validateFileName
} from "cms/files";
import { formatBytes } from "utils/format-bytes";

const focusedStyle = { borderColor: "#2196f3" };
const acceptStyle = { borderColor: "#00e676" };
const rejectStyle = { borderColor: "#ff1744" };

const baseStyle = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    borderWidth: 2,
    borderRadius: 2,
    borderColor: "#eeeeee",
    borderStyle: "dashed",
    backgroundColor: "#fafafa",
    color: "#bdbdbd",
    outline: "none",
    transition: "border .24s ease-in-out",
};

// https://stuarttottle.medium.com/upload-to-azure-blob-storage-with-react-34f37805fdfc
// TODO: add caption, image description, image name/title

// https://stackoverflow.com/questions/62706252/azure-storage-javascript-client-how-to-track-upload-progress-using-ionic-angula
// TODO: upload progress is difficult but might be possible

// Azure uploads
// https://learn.microsoft.com/en-us/azure/active-directory/develop/scenario-spa-acquire-token?tabs=react#acquire-a-token-with-a-pop-up-window

const UploadDocument = (props) => {

    const { onDocumentUploaded } = props;
    const { token } = useAuth();
    const refs = useRef([]);
    const [uploading, setUploading] = useState(false);
    const initialUploadStatus = { status: "PENDING", message: "Your upload has not started. Please click \"Upload Files\"." };
    const [uploadStatus, setUploadStatus] = useState(initialUploadStatus);
    const [files, setFiles] = useState([]);
    const [filesReady, setFilesReady] = useState(false);
    const { getOrgSettingsNumber } = useOrgSettings();
    // const uploadLimit = getOrgSettingsNumber('upload_limit_document'); // value in bytes
    // const uploadLimitMB = Math.floor(uploadLimit/1000000);

    // ---------------------------------------------------------------
    // Dropzone - dropped files are set to 'files', via setFiles
    // ---------------------------------------------------------------

    const dropZoneOptions = {

        // Called when the files are dropped. Sets the file data to state
        onDrop: (acceptedFiles) => {
            Promise.all(
                acceptedFiles.map(async (file) => {
                    Logger.debug(file);
                    let generatedFileName;
                    const isValidFileName = validateFileName(file?.name, "document");
                    
                    const fileExistResp = await SimpliCityAdminSDK.documents.fileExist(token, file?.name);
                    
                    if (isValidFileName && !fileExistResp?.exist ) {
                        generatedFileName = file?.name;
                    } else {
                        generatedFileName = autoGenerateFileName(file?.name);
                        const fileExistResp = await SimpliCityAdminSDK.documents.fileExist(token, generatedFileName);
                        if (fileExistResp?.exist) {
                            generatedFileName = autoGenerateFileName(file?.name, true);
                        }
                    }

                    const newFile = new File([file], generatedFileName, {
                        type: file.type,
                        lastModified: file.lastModified,
                    });
                    
                    return Object.assign(newFile, {
                        preview: URL.createObjectURL(newFile),
                    });
                })
            ).then((resolvedFiles) => {
                setFiles(resolvedFiles);
                setFilesReady(true);
            });
        },
        
        validator: (file) => {
            const file_name = file.name;
            const file_size = file.size;
            // eslint-disable-next-line no-unused-vars
            const [basename, ext] = getBasenameAndExtension(file_name);
            const uploadLimit = getOrgSettingsNumber("upload_limit_document");
            const max_chars = 64;
            // const isImg = isValidImageExtension(ext);
            const isDoc = isValidDocumentExtension(ext);
            const isKnownType = isKnownFileType(file);
            if (!isKnownType) {
                return {
                    code: "type-not-allowed",
                    message: `File type [${file.type}] is is not allowed.`
                };
            }
            if (!isDoc) {
                return {
                    code: "ext-not-allowed",
                    message: "File extension is not allowed."
                };
            }
            if (file_name.length > max_chars) {
                return {
                    code: "name-too-large",
                    message: `Name is larger than ${max_chars} characters.`
                };
            }
            
            if (file_size > uploadLimit) {
                return {
                    code: "file-too-large",
                    message: `File size is larger than ${formatBytes(uploadLimit)}.`
                };
            }
            // must return null if no error
            return null;
        },
        // maxSize: uploadLimit
        // accept: Config.Documents.accept,
    };

    const {
        getRootProps,
        getInputProps,
        isFocused,
        isDragAccept,
        isDragReject,
        // acceptedFiles,
        fileRejections,
    } = useDropzone(dropZoneOptions);

    // const acceptedFileItems = acceptedFiles.map(file => (
    //     <li key={file.path}>
    //         {file.path} - {file.size} bytes
    //     </li>
    // ));

    const fileRejectionItems = fileRejections.map((rejection) => {
        const file = rejection.file;
        const errors = rejection.errors;
        return (
            <div key={file.path}>
                <span style={{ fontWeight: "700" }}>{file.name} - {formatBytes(file.size)} bytes.</span>
                {errors.map((error, index) => (
                    <li key={index}>&nbsp;<span key={error.code}>{error.message}</span></li>
                ))}
            </div>
        );
    });
    
    // ---------------------------------------------------------------
    // main upload function
    // ---------------------------------------------------------------
    const uploadFiles = async () => {
        setUploading(true);
        const uploaded_files = [];
        for (const file of files) {

            // get an User Delegate SAS - for Azure Storage Blob
            const sas_response = await SimpliCityAdminSDK.documents.getToken(token);
            // Token has configuration available inside
            const {
                sas,
                storageAccountName,
                storageAccount,
                containerName,
                fileUrlBase,
            } = sas_response;
            Logger.debug("[UploadDocument] SAS TOKEN RESPONSE", sas_response);

            // *** UPLOAD TO AZURE STORAGE ***
            Logger.debug(`[UploadDocument] Uploading file: ${file.name}`);
            setUploadStatus({ status: "INCOMPLETE", message: "The upload has begun." });
            const uploadResult = await uploadFileAzure(storageAccount, containerName, file, sas);
            const { status, message } = uploadResult;
            setUploadStatus(uploadResult);

            if (status === "ERROR") {
                Logger.error(`[UploadDocument] error. ${message}`, { uploadResult });
                throw Error(message);
            } else {
                const encodedFilename = encodeURIComponent(file.name);
                const storage_url = `${storageAccount}/${containerName}/${encodedFilename}`;
                const public_url = `${fileUrlBase}/${encodedFilename}`;
                const ext = file.name.split(".").pop();
                const payload = {
                    title: `Untitled Document - ${file.name}`,
                    storage_account: storageAccountName,
                    storage_container: containerName,
                    storage_filepath: encodedFilename,
                    storage_url: storage_url,
                    url: public_url,
                    file_name: file.name,
                    file_ext: ext,
                    file_type: file.type,
                    file_size: file.size,
                    language: "en",
                    retention_months: 84,
                    //public: false,  //TODO: remove. now made public by publishing
                    archived: false,
                    audience: "general",
                    // description: '',
                    // doc_id: '',
                    // external_id: '',
                    // classification: '',
                    // sub_classification: '',
                    // owner: '',
                    // author: '',
                };

                // Add record to the database
                try {
                    Logger.debug(`[UploadDocument] create document in DB. ${file.name}`, payload);
                    const response = await SimpliCityAdminSDK.documents.create(token, payload);
                    Logger.debug("[UploadDocument] upload response", { response });
                    uploaded_files.push(response);
                } catch (error) {
                    Logger.error(error);
                }
            }
        }
        onDocumentUploaded(uploaded_files);
        setUploading(false);
        setUploadStatus(initialUploadStatus);
        setFiles([]);
        setFilesReady(false);
    };

    useEffect(() => {
        // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
        return () => files.forEach((file) => URL.revokeObjectURL(file.preview));
    }, [files]);

    // prepare styles
    const dzstyle = useMemo(
        () => ({
            ...baseStyle,
            ...(isFocused ? focusedStyle : {}),
            ...(isDragAccept ? acceptStyle : {}),
            ...(isDragReject ? rejectStyle : {}),
        }),
        [isFocused, isDragAccept, isDragReject],
    );

    return (
        <>
            <div className='upload-container'>
                <div className='upload-inner-container'>

                    {/* -------  DROPZONE  ------- */}
                    <div className="dropzone" {...getRootProps({ dzstyle })} >

                        <input {...getInputProps()} />

                        <div className='upload-container-files'>
                            {isTrue(uploading) &&
                                <div className='uploading-progress'><CircularProgress /></div>
                            }
                            {isFalse(uploading) &&
                                <>
                                    <div className="docs-list">
                                        {files.map((file, index) => {
                                            return (
                                                <DocumentUploadItem
                                                    file={file}
                                                    refs={refs}
                                                    index={index}
                                                    key={index}
                                                />
                                            );
                                        }
                                        )}
                                    </div>
                                    <div className='document-upload-spacer'></div>
                                    {isArrayNotEmpty(fileRejections) &&
                                        <div>
                                            <div style={{ color: "#991111", fontSize: "16px", fontWeight: "700" }}>We removed some files...</div>
                                            <div style={{ color: "#991111", fontSize: "12px" }}>{fileRejectionItems}</div>
                                        </div>
                                    }
                                    <div className='document-upload-instruction'>
                                        <p>
                                            Drag &apos;n&apos; drop, or 
                                            <span
                                                className="simplicity-active-text"
                                            >select some documents</span>
                                        </p>
                                    </div>
                                </>
                            }
                        </div>
                    </div>
                </div>


                {/* -------  Action Bar  ------- */}
                {filesReady && files.length > 0 && (
                    <div className='upload-action-bar'>
                        {/* -------  upload status and button  ------- */}
                        <div className='upload-status-cell'>
                            {uploadStatus.message}
                        </div>

                        <div className='upload-status-cell'>
                            <Button
                                variant='contained'
                                onClick={uploadFiles}
                            >
                                Upload Files
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export { UploadDocument };
